/**
 * ChatbotPanel
 *
 * Resizable panel (right/left/bottom) that hosts the chat UI.
 * Header: agent icon + name, new chat (+), session toggle (hamburger),
 * three-dot menu (⋮), close (X).
 *
 * Requirements: 4.1–4.6, 3.4, 5.1, 10.1
 */

import { useState, useEffect, useRef } from 'react';
import {
  Rocket, Compass, Plus, Menu, MoreVertical, X,
  ArrowRight, ArrowLeft, ArrowDown, Trash2, Download, FileText, ChevronDown, Check, Eye, EyeOff, Search, Maximize2, Minimize2,
} from 'lucide-react';
import { ContextRing } from './ContextRing';
import { PersonaSelectionScreen } from './PersonaSelectionScreen';

const POSITION_STYLES = {
  right: 'fixed top-0 right-0 h-full w-[30vw] min-w-[340px] max-w-[500px] border-l border-border',
  left: 'fixed top-0 left-0 h-full w-[30vw] min-w-[340px] max-w-[500px] border-r border-border',
  bottom: 'fixed bottom-0 left-0 w-full h-[35vh] min-h-[260px] max-h-[50vh] border-t border-border',
};

const MOVE_OPTIONS = [
  { key: 'right', label: 'Right', Icon: ArrowRight },
  { key: 'left', label: 'Left', Icon: ArrowLeft },
  { key: 'bottom', label: 'Bottom', Icon: ArrowDown },
];

const AGENTS = [
  { type: 'pro', name: 'Pro', Icon: Rocket, desc: 'Technical, concise' },
  { type: 'guide', name: 'Guide', Icon: Compass, desc: 'Simple, step-by-step' },
];

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {string} props.agentType - 'pro' | 'guide'
 * @param {string} props.position - 'right' | 'left' | 'bottom'
 * @param {Function} props.onClose
 * @param {Function} props.onNewChat
 * @param {Function} props.onToggleSessions - toggle session sidebar
 * @param {Function} props.onChangePosition
 * @param {Function} props.onClearChat
 * @param {Function} props.onExportChat
 * @param {Function} [props.onExportPdf] - callback for PDF export
 * @param {Object} [props.contextUsage] - context usage metadata for the ring
 * @param {Function} [props.onSwitchAgent] - called with new agent type ('pro' | 'guide')
 * @param {boolean} [props.showPersonaSelection] - when true, render PersonaSelectionScreen instead of children
 * @param {Function} [props.onSelectPersona] - called with persona type ('pro' | 'guide') during initial selection
 * @param {string} [props.searchQuery] - current search query string
 * @param {Function} [props.onSearchQueryChange] - callback to update search query
 * @param {React.ReactNode} props.children - message list + input
 */
export function ChatbotPanel({
  isOpen,
  agentType,
  position = 'right',
  onClose,
  onNewChat,
  onToggleSessions,
  onChangePosition,
  onClearChat,
  onExportChat,
  onExportPdf,
  contextUsage,
  onSwitchAgent,
  showPersonaSelection,
  onSelectPersona,
  searchQuery = '',
  onSearchQueryChange,
  agentOnly = false,
  children,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveSubmenuOpen, setMoveSubmenuOpen] = useState(false);
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(null); // null = use CSS default
  const [sizeMode, setSizeMode] = useState('normal'); // 'normal' | 'wide' | 'full'
  const [transparent, setTransparent] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const agentDropdownRef = useRef(null);
  const isDragging = useRef(false);

  // Close agent dropdown on outside click
  useEffect(() => {
    if (!agentDropdownOpen) return;
    const handler = (e) => {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(e.target)) {
        setAgentDropdownOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [agentDropdownOpen]);

  // Resize drag handler
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      if (position === 'right') {
        const newWidth = Math.max(300, Math.min(window.innerWidth * 0.7, window.innerWidth - e.clientX));
        setPanelWidth(newWidth);
      } else if (position === 'left') {
        const newWidth = Math.max(300, Math.min(window.innerWidth * 0.7, e.clientX));
        setPanelWidth(newWidth);
      }
    };
    const handleMouseUp = () => { isDragging.current = false; document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [position]);

  // Reset custom width when position changes
  useEffect(() => { setPanelWidth(null); }, [position]);

  if (!isOpen) return null;

  const AgentIcon = agentType === 'pro' ? Rocket : Compass;
  const agentName = agentType === 'pro' ? 'Pro' : 'Guide';
  const posStyle = POSITION_STYLES[position] || POSITION_STYLES.right;

  const startResize = () => {
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Build inline style for custom width — sizeMode presets override drag width.
  const sizeStyle = position === 'bottom'
    ? {}
    : sizeMode === 'wide'
      ? { width: '720px', minWidth: '300px', maxWidth: '92vw' }
      : sizeMode === 'full'
        ? { width: '96vw', minWidth: '300px', maxWidth: '96vw' }
        : (panelWidth ? { width: `${panelWidth}px`, minWidth: '300px', maxWidth: '70vw' } : {});
  const panelStyle = {
    ...sizeStyle,
    ...(transparent ? { opacity: 0.35, transition: 'opacity 0.2s ease' } : { opacity: 1, transition: 'opacity 0.2s ease' }),
  };

  return (
    <div
      className={`${posStyle} z-[9980] bg-[#0f1117] dark flex flex-col shadow-2xl`}
      style={panelStyle}
      role="dialog"
      aria-label="Chatbot panel"
    >
      {/* Resize handle — left edge for right panel, right edge for left panel */}
      {position === 'right' && (
        <div
          onMouseDown={startResize}
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500/30 transition-colors z-10"
        />
      )}
      {position === 'left' && (
        <div
          onMouseDown={startResize}
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500/30 transition-colors z-10"
        />
      )}
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700/40 shrink-0">
        {/* Left: agent selector dropdown + context ring */}
        <div className="flex items-center gap-1.5">
          {showPersonaSelection ? (
            <span className="font-medium text-xs text-gray-300 px-1.5 py-0.5">Imago AI</span>
          ) : agentOnly ? (
            /* Agent-only mode: just a clean label, no switcher dropdown */
            <div className="flex items-center gap-1.5 px-1.5 py-0.5">
              <Rocket className="w-4 h-4 text-blue-400" />
              <span className="font-medium text-[13px] text-gray-200">Agent</span>
            </div>
          ) : (
          <div className="relative" ref={agentDropdownRef}>
            <button
              onClick={() => setAgentDropdownOpen(!agentDropdownOpen)}
              className="flex items-center gap-1.5 px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors"
            >
              <AgentIcon className="w-4 h-4 text-gray-400" />
              <span className="font-medium text-xs text-gray-300">{agentName}</span>
              <ChevronDown className="w-3 h-3 text-gray-500" />
            </button>

            {agentDropdownOpen && (
              <div className="absolute left-0 top-full mt-1 w-44 bg-[#1e2028] border border-gray-700/50 rounded-lg shadow-lg py-1 z-50">
                {AGENTS.map(({ type, name, Icon, desc }) => {
                  const isActive = type === agentType;
                  return (
                    <button
                      key={type}
                      onClick={() => {
                        if (type !== agentType) onSwitchAgent?.(type);
                        setAgentDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-white/5 transition-colors ${
                        isActive ? 'text-blue-400' : 'text-gray-400'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium">{name}</div>
                        <div className="text-label text-gray-500">{desc}</div>
                      </div>
                      {isActive && <Check className="w-3 h-3 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          )}
          {!showPersonaSelection && <ContextRing contextUsage={contextUsage} />}
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-0.5">
          {!showPersonaSelection && (
            <>
          {/* New chat */}
          <button
            onClick={onNewChat}
            title="New chat"
            className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>

          {/* Session list toggle */}
          <button
            onClick={onToggleSessions}
            title="Toggle sessions"
            className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors"
          >
            <Menu className="w-3.5 h-3.5" />
          </button>

          {/* Search toggle */}
          <button
            onClick={() => {
              setSearchOpen(!searchOpen);
              onSearchQueryChange?.('');
            }}
            title="Search messages"
            className={`p-1 rounded hover:bg-white/5 transition-colors ${
              searchOpen ? 'text-blue-400' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <Search className="w-3.5 h-3.5" />
          </button>

          {/* Transparency toggle */}
          <button
            onClick={() => setTransparent((t) => !t)}
            title={transparent ? 'Make opaque' : 'Make transparent'}
            className={`p-1 rounded hover:bg-white/5 transition-colors ${
              transparent ? 'text-blue-400' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {transparent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>

          {/* Three-dot menu */}
          <div className="relative">
            <button
              onClick={() => { setMenuOpen(!menuOpen); setMoveSubmenuOpen(false); }}
              title="More options"
              className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors"
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-[#1e2028] border border-gray-700/50 rounded-lg shadow-lg py-1 z-50">
                {/* Move chatbot to */}
                <div
                  className="relative"
                  onMouseEnter={() => setMoveSubmenuOpen(true)}
                  onMouseLeave={() => setMoveSubmenuOpen(false)}
                >
                  <button className="w-full text-left h-row-compact px-3 text-xs text-gray-400 hover:bg-white/5 flex items-center justify-between">
                    <ArrowLeft className="w-3 h-3" />
                    <span className="flex-1 ml-2">Move chatbot to</span>
                  </button>
                  {moveSubmenuOpen && (
                    <div className="absolute right-full top-0 -mr-1 pr-1 w-32">
                      <div className="bg-[#1e2028] border border-gray-700/50 rounded-lg shadow-lg py-1">
                      {MOVE_OPTIONS.map(({ key, label, Icon }) => (
                        <button
                          key={key}
                          onClick={() => { onChangePosition?.(key); setMenuOpen(false); }}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 flex items-center gap-2 ${
                            position === key ? 'text-blue-400 font-medium' : 'text-gray-400'
                          }`}
                        >
                          <Icon className="w-3 h-3" /> {label}
                        </button>
                      ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => { onClearChat?.(); setMenuOpen(false); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-white/5 flex items-center gap-2"
                >
                  <Trash2 className="w-3 h-3" /> Clear chat
                </button>

                <button
                  onClick={() => { onExportChat?.(); setMenuOpen(false); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-white/5 flex items-center gap-2"
                >
                  <Download className="w-3 h-3" /> Export as Markdown
                </button>

                <button
                  onClick={() => { onExportPdf?.(); setMenuOpen(false); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-white/5 flex items-center gap-2"
                >
                  <FileText className="w-3 h-3" /> Export as PDF
                </button>
              </div>
            )}
          </div>
            </>
          )}

          {/* Expand / full-screen (cycles normal → wide → full) */}
          {position !== 'bottom' && (
            <button
              onClick={() => setSizeMode((m) => (m === 'normal' ? 'wide' : m === 'wide' ? 'full' : 'normal'))}
              title={sizeMode === 'full' ? 'Restore size' : sizeMode === 'wide' ? 'Full screen' : 'Expand'}
              className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors"
            >
              {sizeMode === 'full' ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </button>
          )}

          {/* Close */}
          <button
            onClick={onClose}
            title="Close"
            className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Search bar — conditionally rendered below header */}
      {searchOpen && !showPersonaSelection && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-700/40 shrink-0">
          <Search className="w-3.5 h-3.5 text-gray-500 shrink-0" />
          <input
            autoFocus
            value={searchQuery}
            onChange={(e) => onSearchQueryChange?.(e.target.value)}
            placeholder="Search messages..."
            className="flex-1 bg-transparent text-sm text-gray-300 outline-none placeholder-gray-600"
          />
          <button
            onClick={() => {
              setSearchOpen(false);
              onSearchQueryChange?.('');
            }}
            title="Close search"
            className="p-0.5 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Body — persona selection or children (session sidebar, message list, input) */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {showPersonaSelection ? (
          <PersonaSelectionScreen onSelect={onSelectPersona} />
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export default ChatbotPanel;
